'use client'

import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Router,
  Network,
  Globe,
  Activity,
  Zap,
  FileWarning,
  Info
} from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert"
import { extractViolationFlows } from '@/lib/violation-utils'

type UnknownRecord = Record<string, unknown>

export type NormalizedViolation = {
  type: string
  severity: string
  device?: string
  interface?: string
  source?: string
  destination?: string
  peer?: string
  filename?: string
  file?: string
  path?: string
  message?: string
  name?: string
  description?: string
  remediation?: string
  sources?: string[]
  destinations?: string[]
  disposition?: string
  details?: string | UnknownRecord
  sample_flows?: Array<Record<string, unknown>>
  flows?: Array<Record<string, unknown>>
  line_numbers?: Array<number | string>
  lineNumbers?: Array<number | string>
  findings?: Array<UnknownRecord>
} & UnknownRecord

const toLineNumbers = (violation: NormalizedViolation): string => {
  const combined = [
    ...(Array.isArray(violation.line_numbers) ? violation.line_numbers : []),
    ...(Array.isArray(violation.lineNumbers) ? violation.lineNumbers : []),
  ]

  const formatted = combined
    .map((value) => {
      if (typeof value === 'number') return value.toString()
      if (typeof value === 'string') return value
      return ''
    })
    .filter(Boolean)

  return formatted.length ? formatted.join(', ') : ''
}

const normalizeFindings = (violation: NormalizedViolation): Array<UnknownRecord> => {
  if (!Array.isArray(violation.findings)) return []
  return violation.findings.filter((finding): finding is UnknownRecord =>
    Boolean(finding && typeof finding === 'object')
  )
}

interface InvariantResult {
  name: string
  type: string
  status: string
  severity: string
  description?: string
  message: string
  details?: {
    findings?: string
    violations?: NormalizedViolation[]
    total_sessions?: number
    established_sessions?: number
    down_sessions?: number
    session_list?: unknown[]
    paths_found?: number
    paths?: unknown[]
    mtu_values?: number[]
    mtu_groups?: unknown[]
    total_interfaces?: number
    [key: string]: unknown
  }
}

interface InvariantDetailsProps {
  invariants: InvariantResult[] | Record<string, InvariantResult> | null | undefined
  summary?: {
    total: number
    passed: number
    failed: number
    warnings: number
    errors: number
  } | null
}

const getInvariantIcon = (type: string) => {
  switch (type) {
    case 'reachability':
      return <Network className="h-4 w-4" />
    case 'bgp_sessions':
    case 'bgp_session_status':
      return <Router className="h-4 w-4" />
    case 'mtu_consistency':
      return <Activity className="h-4 w-4" />
    case 'route_propagation':
    case 'bgp_routes':
      return <Globe className="h-4 w-4" />
    case 'configuration':
      return <FileWarning className="h-4 w-4" />
    case 'failure_impact':
      return <Zap className="h-4 w-4" />
    default:
      return <Info className="h-4 w-4" />
  }
}

const getStatusBadge = (status: string, _severity?: string) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    passed: 'default',
    failed: 'destructive',
    warning: 'secondary',
    error: 'destructive',
  }
  
  const colors: Record<string, string> = {
    passed: 'bg-green-100 text-green-800 hover:bg-green-200',
    failed: 'bg-red-100 text-red-800 hover:bg-red-200',
    warning: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
    error: 'bg-red-100 text-red-800 hover:bg-red-200',
  }

  return (
    <Badge 
      variant={variants[status] || 'outline'}
      className={colors[status] || ''}
    >
      {status.toUpperCase()}
    </Badge>
  )
}

const getSeverityBadge = (severity: string) => {
  const severityLower = severity.toLowerCase()

  // Use explicit conditional rendering for Tailwind JIT to pick up all classes
  const badgeClass = severityLower === 'critical'
    ? 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200'
    : severityLower === 'high'
    ? 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200'
    : severityLower === 'medium'
    ? 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200'
    : severityLower === 'low'
    ? 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200'
    : severityLower === 'warning'
    ? 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200'
    : ''

  return (
    <Badge variant="outline" className={badgeClass}>
      {severity.toUpperCase()}
    </Badge>
  )
}

export const ViolationCard = ({ violation }: { violation: NormalizedViolation }) => {
  const location = violation.filename ?? violation.file ?? violation.path
  const detailsText = typeof violation.details === 'string' ? violation.details : ''
  const descriptionText = typeof violation.description === 'string' ? violation.description : ''
  const remediationText = typeof violation.remediation === 'string'
    ? violation.remediation.trim()
    : ''
  const primaryText =
    violation.message ??
    descriptionText ??
    detailsText ??
    ''
  const secondaryText = (() => {
    if (violation.message && descriptionText && descriptionText !== violation.message) {
      return descriptionText
    }
    if (violation.message && detailsText && detailsText !== violation.message) {
      return detailsText
    }
    if (!violation.message && descriptionText && detailsText && descriptionText !== detailsText) {
      return detailsText
    }
    return undefined
  })()
  const flowEntries = extractViolationFlows(violation)
  const lineNumbersText = toLineNumbers(violation)
  const findings = normalizeFindings(violation)

  return (
    <div className="border-l-4 border-red-500 bg-red-50 p-3 rounded-r-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-sm">{violation.type}</span>
            {violation.severity && getSeverityBadge(String(violation.severity))}
          </div>
          {primaryText && (
            <p className="text-sm text-foreground mb-1">{primaryText}</p>
          )}
          {secondaryText && typeof secondaryText === 'string' && (
            <p className="text-xs text-muted-foreground mb-1">{secondaryText}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {violation.name && (
              <span>Check: <code className="bg-muted px-1 rounded">{violation.name}</code></span>
            )}
            {location && (
              <span>File: <code className="bg-muted px-1 rounded">{location}</code></span>
            )}
            {violation.device && (
              <span>Device: <code className="bg-muted px-1 rounded">{violation.device}</code></span>
            )}
            {violation.interface && (
              <span>Interface: <code className="bg-muted px-1 rounded">{violation.interface}</code></span>
            )}
            {violation.peer && (
              <span>Peer: <code className="bg-muted px-1 rounded">{violation.peer}</code></span>
            )}
            {violation.source && (
              <span>Source: <code className="bg-muted px-1 rounded">{violation.source}</code></span>
            )}
            {violation.destination && (
              <span>Destination: <code className="bg-muted px-1 rounded">{violation.destination}</code></span>
            )}
            {lineNumbersText && (
              <span>Lines: <code className="bg-muted px-1 rounded">{lineNumbersText}</code></span>
            )}
          </div>
          {flowEntries.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Impacted Flows
              </p>
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {flowEntries.map((flow, idx) => (
                  <li key={`${flow.source ?? flow.raw ?? 'flow'}-${idx}`}>
                    {flow.source && flow.destination ? (
                      <>
                        <code className="bg-muted px-1 rounded">{flow.source}</code>
                        <span className="mx-1">-&gt;</span>
                        <code className="bg-muted px-1 rounded">{flow.destination}</code>
                        {flow.disposition && (
                          <span className="ml-2 uppercase tracking-wide text-[0.65rem]">
                            {flow.disposition}
                          </span>
                        )}
                      </>
                    ) : (
                      flow.raw && <span>{flow.raw}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {findings.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Findings
              </p>
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {findings.map((finding, idx) => {
                  const message =
                    typeof finding.message === 'string'
                      ? finding.message
                      : typeof finding.description === 'string'
                        ? finding.description
                        : ''
                  const filename =
                    typeof finding.filename === 'string' ? finding.filename : undefined
                  const lineNumbers = Array.isArray(finding.line_numbers)
                    ? finding.line_numbers.filter((value) => typeof value === 'number' || typeof value === 'string')
                    : []

                  return (
                    <li key={`${message || filename || 'finding'}-${idx}`}>
                      {message && <span>{message}</span>}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        {filename && (
                          <span>File: <code className="bg-muted px-1 rounded">{filename}</code></span>
                        )}
                        {lineNumbers.length > 0 && (
                          <span>
                            Lines:{' '}
                            <code className="bg-muted px-1 rounded">
                              {lineNumbers.join(', ')}
                            </code>
                          </span>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          {remediationText && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Remediation
              </p>
              <pre className="mt-1 whitespace-pre-wrap text-xs bg-muted p-2 rounded-md text-muted-foreground">
                {remediationText}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function InvariantDetails({ invariants, summary }: InvariantDetailsProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const toggleExpanded = (name: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(name)) {
      newExpanded.delete(name)
    } else {
      newExpanded.add(name)
    }
    setExpandedItems(newExpanded)
  }

  // Convert invariants to array if it's an object or handle null/undefined
  const invariantsList = Array.isArray(invariants)
    ? invariants
    : invariants && typeof invariants === 'object'
      ? Object.values(invariants)
      : []

  // Group invariants by status for better organization
  const failedInvariants = invariantsList.filter(i => i.status === 'failed')
  const warningInvariants = invariantsList.filter(i => i.status === 'warning')
  const passedInvariants = invariantsList.filter(i => i.status === 'passed')

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>Validation Summary</CardTitle>
            <CardDescription>Overview of all invariant checks performed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">{summary.total}</p>
                <p className="text-sm text-blue-600">Total Checks</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{summary.passed}</p>
                <p className="text-sm text-green-600">Passed</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-700">{summary.failed}</p>
                <p className="text-sm text-red-600">Failed</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-700">{summary.warnings}</p>
                <p className="text-sm text-yellow-600">Warnings</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold text-foreground">{summary.errors}</p>
                <p className="text-sm text-muted-foreground">Errors</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failed Invariants - Show prominently */}
      {failedInvariants.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="h-5 w-5" />
              Failed Invariants ({failedInvariants.length})
            </CardTitle>
            <CardDescription>These checks failed and require attention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {failedInvariants.map((invariant, index) => (
              <InvariantCard key={index} invariant={invariant} expanded={expandedItems.has(invariant.name)} onToggle={() => toggleExpanded(invariant.name)} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Warning Invariants */}
      {warningInvariants.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700">
              <AlertCircle className="h-5 w-5" />
              Warnings ({warningInvariants.length})
            </CardTitle>
            <CardDescription>These checks raised warnings that should be reviewed</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {warningInvariants.map((invariant, index) => (
              <InvariantCard key={index} invariant={invariant} expanded={expandedItems.has(invariant.name)} onToggle={() => toggleExpanded(invariant.name)} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Passed Invariants - Collapsed by default */}
      {passedInvariants.length > 0 && (
        <Card className="border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              Passed Invariants ({passedInvariants.length})
            </CardTitle>
            <CardDescription>These checks passed successfully</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {passedInvariants.map((invariant, index) => (
              <InvariantCard key={index} invariant={invariant} expanded={expandedItems.has(invariant.name)} onToggle={() => toggleExpanded(invariant.name)} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// Individual Invariant Card Component
function InvariantCard({ 
  invariant, 
  expanded, 
  onToggle 
}: { 
  invariant: InvariantResult
  expanded: boolean
  onToggle: () => void 
}) {
  const hasDetails = invariant.details && Object.keys(invariant.details).length > 0
  const hasViolations = invariant.details?.violations && invariant.details.violations.length > 0

  return (
    <Collapsible open={expanded} onOpenChange={onToggle}>
      <Card className={`${invariant.status === 'failed' ? 'border-red-200' : ''}`}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 hover:bg-muted rounded-t-lg">
            <div className="flex items-center gap-3">
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {getInvariantIcon(invariant.type)}
              <div className="text-left">
                <p className="font-semibold">{invariant.name}</p>
                {invariant.description && (
                  <p className="text-sm text-muted-foreground">{invariant.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Only show severity badge if it's different from status or if status is failed */}
              {(invariant.status === 'failed' || 
                (invariant.status === 'warning' && invariant.severity !== 'warning')) && 
                getSeverityBadge(invariant.severity)}
              {getStatusBadge(invariant.status)}
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4">
            {/* Main message */}
            <Alert className="mb-4">
              <AlertDescription>{invariant.message}</AlertDescription>
            </Alert>

            {/* Findings */}
            {invariant.details?.findings && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Findings</h4>
                <p className="text-sm text-foreground bg-muted p-3 rounded-lg">
                  {invariant.details.findings}
                </p>
              </div>
            )}

            {/* Violations */}
            {hasViolations && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Violations ({invariant.details!.violations!.length})</h4>
                <div className="space-y-2">
                  {invariant.details!.violations!.map((violation, idx) => (
                    <ViolationCard key={idx} violation={violation} />
                  ))}
                </div>
              </div>
            )}

            {/* BGP Session Details */}
            {invariant.details?.session_list && invariant.details.session_list.length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2">BGP Sessions</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-3 py-2 text-left">Node</th>
                        <th className="px-3 py-2 text-left">Remote Node</th>
                        <th className="px-3 py-2 text-left">Local AS</th>
                        <th className="px-3 py-2 text-left">Remote AS</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invariant.details.session_list as unknown[]).slice(0, 5).map((session: unknown, idx: number) => {
                        const s = session as Record<string, unknown>;
                        return (
                        <tr key={idx} className="border-b">
                          <td className="px-3 py-2">{String(s.node ?? '')}</td>
                          <td className="px-3 py-2">{String(s.remote_node ?? '')}</td>
                          <td className="px-3 py-2">{String(s.local_as ?? '')}</td>
                          <td className="px-3 py-2">{String(s.remote_as ?? '')}</td>
                          <td className="px-3 py-2">
                            <Badge variant={s.status === 'ESTABLISHED' ? 'default' : 'destructive'} className="text-xs">
                              {String(s.status ?? '')}
                            </Badge>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                  {(invariant.details.session_list as unknown[]).length > 5 && (
                    <p className="text-sm text-muted-foreground mt-2">
                      ... and {(invariant.details.session_list as unknown[]).length - 5} more sessions
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* MTU Groups */}
            {invariant.details?.mtu_groups && (invariant.details.mtu_groups as unknown[]).length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2">MTU Distribution</h4>
                <div className="space-y-2">
                  {(invariant.details.mtu_groups as unknown[]).map((group: unknown, idx: number) => {
                    const g = group as Record<string, unknown>;
                    return (
                    <div key={idx} className="bg-muted p-3 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">MTU {String(g.mtu_value)}</span>
                        <Badge variant="outline">{String(g.interface_count)} interfaces</Badge>
                      </div>
                      {g.interfaces && Array.isArray(g.interfaces) && g.interfaces.length > 0 ? (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {g.interfaces.slice(0, 3).map((intf: unknown, i: number) => {
                            const iface = intf as Record<string, unknown>;
                            return (
                            <div key={i}>{String(iface.device)}: {String(iface.interface)}</div>
                          );})}
                          {g.interfaces.length > 3 && (
                            <div>... and {g.interfaces.length - 3} more</div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                </div>
              </div>
            )}

            {/* Additional Details */}
            {hasDetails && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  View Raw Details
                </summary>
                <pre className="mt-2 text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                  {JSON.stringify(invariant.details, null, 2)}
                </pre>
              </details>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
